import type { WaveDef } from "../types";

/**
 * 20 waves, each with a DISTINCT THEME and creep roster so no two waves feel
 * the same. New specialty creeps (skeleton/spider/shadow/vampire/demon/
 * crystal/magma_knight) headline their own waves, then mix into the late-game
 * swarm. Difficulty escalates: more types, denser spawns, tougher anchors.
 *
 * Wave themes:
 *   1  Bone Vanguard        skeleton + grunt
 *   2  Scout Swarm          spider + fast
 *   3  Shadow Creep         shadow + skeleton + grunt
 *   4  Web Nest             spider + shadow + fast
 *   5  Blood Moon (+boss)   vampire + skeleton + boss
 *   6  MEGA: Overlord       solo
 *   7  Demon Raid           demon + hellhound
 *   8  Phantom Pack         shadow + spider + hellhound
 *   9  Crystal Guard        crystal + skeleton + tank
 *   10 Magma Vanguard (+3 boss) magma_knight + demon + boss
 *   11 Vampire Coven        vampire + shadow + elite
 *   12 MEGA: Warlord        solo
 *   13 Demon Legion         demon + crystal + spider
 *   14 Forge of Magma       magma_knight + golem + hellhound
 *   15 Full Bestiary (+boss) mixed + boss
 *   16 Shadow Storm         shadow + spider + vampire + titan
 *   17 Infernal Host        demon + vampire + magma_knight + titan
 *   18 MEGA: World Ender    solo
 *   19 The Reckoning        everything escalating
 *   20 Apocalypse           full swarm, all types + 6 bosses
 */
export const WAVES: WaveDef[] = [
  // ---- EARLY GAME (1-5) ----
  // Wave 1 — Bone Vanguard: skeletons lead, grunts follow.
  {
    index: 1,
    spawns: [
      { enemy: "skeleton", count: 14, interval: 420 },
      { enemy: "grunt", count: 10, interval: 480, delay: 2000 },
    ],
    bonusGold: 30,
  },
  // Wave 2 — Scout Swarm: fast skittering spiders.
  {
    index: 2,
    spawns: [
      { enemy: "spider", count: 16, interval: 320 },
      { enemy: "fast", count: 8, interval: 300, delay: 2500 },
    ],
    bonusGold: 40,
  },
  // Wave 3 — Shadow Creep: silent fast shadows woven through bones.
  {
    index: 3,
    spawns: [
      { enemy: "grunt", count: 18, interval: 320 },
      { enemy: "shadow", count: 12, interval: 240, delay: 1600 },
      { enemy: "skeleton", count: 10, interval: 360, delay: 3200 },
    ],
    bonusGold: 55,
  },
  // Wave 4 — Web Nest: spider flood backed by shadows.
  {
    index: 4,
    spawns: [
      { enemy: "spider", count: 26, interval: 200 },
      { enemy: "shadow", count: 14, interval: 220, delay: 1400 },
      { enemy: "fast", count: 12, interval: 280, delay: 3000 },
    ],
    bonusGold: 70,
  },
  // Wave 5 — Blood Moon: vampires drain through, then a boss.
  {
    index: 5,
    spawns: [
      { enemy: "skeleton", count: 18, interval: 240 },
      { enemy: "vampire", count: 10, interval: 360, delay: 1500 },
      { enemy: "shadow", count: 12, interval: 260, delay: 3000 },
      { enemy: "boss", count: 1, interval: 0, delay: 6000 },
    ],
    bonusGold: 110,
  },

  // ---- MID GAME (6-10) ----
  // Wave 6 — SOLO MEGA-BOSS: Overlord. Lone monster, slow march.
  {
    index: 6,
    spawns: [
      { enemy: "overlord", count: 1, interval: 0, delay: 1200 },
    ],
    bonusGold: 250,
  },
  // Wave 7 — Demon Raid: heavy demons escorted by hellhounds.
  {
    index: 7,
    spawns: [
      { enemy: "hellhound", count: 18, interval: 260 },
      { enemy: "demon", count: 8, interval: 700, delay: 2000 },
      { enemy: "fast", count: 18, interval: 220, delay: 3500 },
    ],
    bonusGold: 110,
  },
  // Wave 8 — Phantom Pack: shadows + spiders + hounds, fast chaos.
  {
    index: 8,
    spawns: [
      { enemy: "shadow", count: 22, interval: 180 },
      { enemy: "spider", count: 20, interval: 200, delay: 1200 },
      { enemy: "hellhound", count: 12, interval: 320, delay: 2600 },
      { enemy: "grunt", count: 18, interval: 280, delay: 3500 },
    ],
    bonusGold: 130,
  },
  // Wave 9 — Crystal Guard: armored crystals + skeletons + a tank line.
  {
    index: 9,
    spawns: [
      { enemy: "skeleton", count: 26, interval: 200 },
      { enemy: "crystal", count: 8, interval: 900, delay: 2000 },
      { enemy: "spider", count: 18, interval: 220, delay: 3000 },
      { enemy: "tank", count: 6, interval: 1100, delay: 4500 },
    ],
    bonusGold: 160,
  },
  // Wave 10 — Magma Vanguard: magma knights + demons, capped by 3 bosses.
  {
    index: 10,
    spawns: [
      { enemy: "hellhound", count: 24, interval: 180 },
      { enemy: "demon", count: 10, interval: 600, delay: 1500 },
      { enemy: "magma_knight", count: 6, interval: 1200, delay: 3000 },
      { enemy: "boss", count: 3, interval: 4500, delay: 7000 },
    ],
    bonusGold: 280,
  },

  // ---- HARD MODE (11-15) ----
  // Wave 11 — Vampire Coven: vampires + shadows + elites.
  {
    index: 11,
    spawns: [
      { enemy: "vampire", count: 18, interval: 300 },
      { enemy: "shadow", count: 24, interval: 170, delay: 1200 },
      { enemy: "elite", count: 14, interval: 420, delay: 2500 },
      { enemy: "skeleton", count: 20, interval: 220, delay: 4000 },
    ],
    bonusGold: 200,
  },
  // Wave 12 — SOLO MEGA-BOSS: Warlord. Mid-game escalation.
  {
    index: 12,
    spawns: [
      { enemy: "warlord", count: 1, interval: 0, delay: 1500 },
    ],
    bonusGold: 500,
  },
  // Wave 13 — Demon Legion: demons + crystals + spider tide.
  {
    index: 13,
    spawns: [
      { enemy: "spider", count: 30, interval: 160 },
      { enemy: "demon", count: 14, interval: 500, delay: 1500 },
      { enemy: "crystal", count: 10, interval: 800, delay: 2800 },
      { enemy: "hellhound", count: 18, interval: 240, delay: 4000 },
    ],
    bonusGold: 240,
  },
  // Wave 14 — Forge of Magma: magma knights + golems + hounds.
  {
    index: 14,
    spawns: [
      { enemy: "hellhound", count: 32, interval: 150 },
      { enemy: "magma_knight", count: 12, interval: 700, delay: 1800 },
      { enemy: "golem", count: 5, interval: 1400, delay: 3500 },
      { enemy: "demon", count: 12, interval: 520, delay: 5000 },
    ],
    bonusGold: 280,
  },
  // Wave 15 — Full Bestiary: every specialty type in rotation + a boss.
  {
    index: 15,
    spawns: [
      { enemy: "skeleton", count: 28, interval: 180 },
      { enemy: "spider", count: 24, interval: 180, delay: 1200 },
      { enemy: "vampire", count: 16, interval: 320, delay: 2000 },
      { enemy: "shadow", count: 24, interval: 170, delay: 3000 },
      { enemy: "crystal", count: 10, interval: 700, delay: 4200 },
      { enemy: "boss", count: 3, interval: 4000, delay: 7000 },
    ],
    bonusGold: 360,
  },

  // ---- LATE GAME (16-20) — full chaos, all types ----
  // Wave 16 — Shadow Storm: shadows + spiders + vampires under a titan.
  {
    index: 16,
    spawns: [
      { enemy: "shadow", count: 40, interval: 130 },
      { enemy: "spider", count: 30, interval: 160, delay: 1200 },
      { enemy: "vampire", count: 22, interval: 260, delay: 2200 },
      { enemy: "elite", count: 22, interval: 280, delay: 3500 },
      { enemy: "titan", count: 4, interval: 2600, delay: 6000 },
    ],
    bonusGold: 320,
  },
  // Wave 17 — Infernal Host: demons + vampires + magma knights + titans.
  {
    index: 17,
    spawns: [
      { enemy: "hellhound", count: 36, interval: 140 },
      { enemy: "demon", count: 18, interval: 420, delay: 1500 },
      { enemy: "vampire", count: 24, interval: 240, delay: 2600 },
      { enemy: "magma_knight", count: 12, interval: 800, delay: 4000 },
      { enemy: "titan", count: 6, interval: 2300, delay: 6500 },
    ],
    bonusGold: 380,
  },
  // Wave 18 — SOLO MEGA-BOSS: World Ender. Final escalation before the swarm.
  {
    index: 18,
    spawns: [
      { enemy: "worldender", count: 1, interval: 0, delay: 1800 },
    ],
    bonusGold: 900,
  },
  // Wave 19 — The Reckoning: every creep type escalating into the lane.
  {
    index: 19,
    spawns: [
      { enemy: "skeleton", count: 36, interval: 150 },
      { enemy: "spider", count: 36, interval: 150, delay: 1000 },
      { enemy: "shadow", count: 36, interval: 140, delay: 2000 },
      { enemy: "vampire", count: 26, interval: 240, delay: 2800 },
      { enemy: "demon", count: 16, interval: 460, delay: 3800 },
      { enemy: "crystal", count: 10, interval: 900, delay: 5000 },
      { enemy: "magma_knight", count: 8, interval: 1100, delay: 6000 },
      { enemy: "boss", count: 2, interval: 5000, delay: 9000 },
    ],
    bonusGold: 480,
  },
  // Wave 20 — Apocalypse: full swarm, all types + 6 bosses.
  {
    index: 20,
    spawns: [
      { enemy: "skeleton", count: 40, interval: 150 },
      { enemy: "spider", count: 44, interval: 130, delay: 800 },
      { enemy: "shadow", count: 44, interval: 130, delay: 1600 },
      { enemy: "hellhound", count: 36, interval: 160, delay: 2400 },
      { enemy: "vampire", count: 28, interval: 220, delay: 3000 },
      { enemy: "demon", count: 20, interval: 400, delay: 3800 },
      { enemy: "crystal", count: 12, interval: 800, delay: 5000 },
      { enemy: "magma_knight", count: 12, interval: 900, delay: 5800 },
      { enemy: "titan", count: 8, interval: 1900, delay: 6500 },
      { enemy: "boss", count: 6, interval: 3200, delay: 8000 },
    ],
    bonusGold: 800,
  },
];
